/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useCallback, useEffect, useState } from "react";
import { MsgType, type MatrixClient } from "matrix-js-sdk";
import { Button } from "@vector-im/compound-web";
import copy from "copy-to-clipboard";

import { type UrlParams } from "../UrlParams";
import { Config } from "../config/Config";
import { Modal } from "../Modal";
import styles from "./VocemModeration.module.css";

// El SDK avisa solo al primer miembro; las entradas posteriores no vuelven a llamar.
export function vocemCallNotificationType(
  requested: UrlParams["sendNotificationType"],
  ongoing = false,
): UrlParams["sendNotificationType"] {
  return Config.get().vocem_moderation_url
    ? ongoing
      ? undefined
      : "ring"
    : requested;
}

export function vocemSessionBehavior(
  autoLeave: boolean,
  waitForPickup: boolean,
) {
  return Config.get().vocem_moderation_url
    ? { autoLeaveWhenOthersLeft: false, waitForCallPickup: false }
    : { autoLeaveWhenOthersLeft: autoLeave, waitForCallPickup: waitForPickup };
}

export interface ModerationState {
  call_id?: string | null;
  moderator: boolean;
  starter: string | null;
  join_url: string;
  participants: {
    identity: string;
    user_id: string | null;
    name: string;
    audio_muted: boolean;
    can_moderate: boolean;
  }[];
}

export async function vocemRequest(
  client: MatrixClient,
  roomId: string,
  action: string,
  identity?: string,
): Promise<ModerationState | null> {
  const endpoint = Config.get().vocem_moderation_url;
  if (!endpoint) return null;
  const response = await fetch(`${endpoint}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      room_id: roomId,
      openid_token: await client.getOpenIdToken(),
      identity,
    }),
  });
  if (!response.ok)
    throw new Error("No se pudo verificar la moderación de la llamada.");
  return (await response.json()) as ModerationState;
}

export async function announceVocemCall(
  client: MatrixClient,
  roomId: string,
  state: ModerationState | null,
): Promise<void> {
  if (
    !state?.call_id ||
    state.starter !== client.getUserId() ||
    state.participants.length !== 0
  )
    return;
  const users =
    client
      .getRoom(roomId)
      ?.getJoinedMembers()
      .map((m) => m.userId)
      .filter((u) => u !== client.getUserId()) ?? [];
  await client.sendMessage(
    roomId,
    {
      msgtype: MsgType.Text,
      body: "Videollamada iniciada. Entrar a la sesión: " + state.join_url,
      "m.mentions": { user_ids: users },
    },
    "vocem-call-" + state.call_id,
  );
}

export interface ModerationViewProps {
  open: boolean;
  state: ModerationState | null;
  busy: boolean;
  error: string;
  copied: boolean;
  self: string | null;
  onOpen: () => void;
  onClose: () => void;
  onCopy: () => void;
  onAction: (action: "mute" | "remove", identity: string) => void;
}

export function VocemModerationView(props: ModerationViewProps) {
  const [confirm, setConfirm] = useState<string | null>(null);
  return (
    <>
      <Button size="md" kind="secondary" onClick={props.onOpen}>
        Participantes y enlace
      </Button>
      <Modal
        title="Participantes y enlace"
        hideHeader
        className={styles.modal}
        open={props.open}
        onDismiss={props.onClose}
      >
        <div className={styles.panel}>
          <header className={styles.heading}>
            <div>
              <h2>Tu sesión</h2>
              <span className={styles.subtitle}>
                {props.state?.moderator ? "Moderador" : "Participante"}
              </span>
            </div>
            <button
              className={styles.close}
              onClick={props.onClose}
              aria-label="Cerrar"
            >
              ×
            </button>
          </header>
          {props.error && (
            <p className={styles.notice} role="alert">
              {props.error}
            </p>
          )}
          {!props.state && !props.error && (
            <p role="status">Cargando participantes…</p>
          )}
          {props.state && (
            <>
              <div className={styles.share}>
                <Button size="md" kind="secondary" onClick={props.onCopy}>
                  {props.copied
                    ? "Enlace copiado"
                    : "Copiar enlace de la sesión"}
                </Button>
                <details className={styles.help}>
                  <summary>¿Quién puede entrar?</summary>
                  <p>
                    El enlace requiere acceso al grupo. Invita primero a los
                    compañeros que todavía no pertenecen a él.
                  </p>
                </details>
              </div>
              <div className={styles.section}>
                Participantes <span>{props.state.participants.length}</span>
              </div>
              <div className={styles.list}>
                {props.state.participants.length === 0 && (
                  <p>No hay participantes conectados.</p>
                )}
                {props.state.participants.map((p) => (
                  <div className={styles.person} key={p.identity}>
                    <div className={styles.identity}>
                      <span className={styles.avatar} aria-hidden="true">
                        {p.name.replace(/^@/, "").slice(0, 1).toUpperCase()}
                      </span>
                      <div className={styles.name}>
                        <strong>
                          {p.name.startsWith("@")
                            ? p.name.slice(1).split(":")[0]
                            : p.name}
                          {p.user_id === props.self && (
                            <span className={styles.you}>Tú</span>
                          )}
                        </strong>
                        <span className={styles.subtitle}>
                          {p.audio_muted
                            ? "Micrófono silenciado"
                            : "En la llamada"}
                        </span>
                      </div>
                    </div>
                    {props.state?.moderator &&
                      p.can_moderate &&
                      p.user_id &&
                      p.user_id !== props.self && (
                        <>
                          <div className={styles.actions}>
                            <Button
                              size="md"
                              kind="secondary"
                              disabled={props.busy}
                              onClick={() => props.onAction("mute", p.identity)}
                            >
                              Silenciar
                            </Button>
                            <Button
                              size="md"
                              kind="secondary"
                              disabled={props.busy}
                              onClick={() => setConfirm(p.identity)}
                            >
                              Sacar de la llamada
                            </Button>
                          </div>
                          {confirm === p.identity && (
                            <div className={styles.confirm}>
                              <p>
                                Se desconectará de la llamada. Podrá volver a
                                entrar si conserva acceso a la sala.
                              </p>
                              <div className={styles.actions}>
                                <Button
                                  size="md"
                                  disabled={props.busy}
                                  onClick={() => {
                                    props.onAction("remove", p.identity);
                                    setConfirm(null);
                                  }}
                                >
                                  Confirmar salida
                                </Button>
                                <Button
                                  size="md"
                                  kind="secondary"
                                  onClick={() => setConfirm(null)}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                  </div>
                ))}
              </div>
            </>
          )}
          <footer className={styles.footer}>
            <details>
              <summary>Acerca de Vocem</summary>
              <a href="/vocem-source.tar.gz">
                Código fuente de esta versión de Vocem
              </a>
            </details>
          </footer>
        </div>
      </Modal>
    </>
  );
}

export function VocemModeration({
  client,
  roomId,
}: {
  client: MatrixClient;
  roomId: string;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ModerationState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const next = await vocemRequest(client, roomId, "status");
      if (next)
        next.participants = next.participants.map((participant) => ({
          ...participant,
          name:
            (participant.user_id &&
              client.getRoom(roomId)?.getMember(participant.user_id)?.name) ||
            participant.name,
        }));
      setState(next);
      setError("");
    } catch {
      setState(null);
      setError("No se pudo verificar la llamada. Vuelve a abrir este panel.");
    }
  }, [client, roomId]);
  useEffect(() => {
    if (!open) return;
    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, 5000);
    return () => clearInterval(timer);
  }, [open, refresh]);
  if (!Config.get().vocem_moderation_url) return null;
  const action = async (
    kind: "mute" | "remove",
    identity: string,
  ): Promise<void> => {
    setBusy(true);
    try {
      await vocemRequest(client, roomId, kind, identity);
      await refresh();
    } catch {
      setError(
        "No se pudo aplicar la acción. Revisa tu rol o vuelve a intentarlo.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <VocemModerationView
      open={open}
      state={state}
      busy={busy}
      error={error}
      copied={copied}
      self={client.getUserId()}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onCopy={() => setCopied(copy(state?.join_url ?? ""))}
      onAction={(kind, id) => {
        void action(kind, id);
      }}
    />
  );
}
